import {useReducer, useRef, useCallback} from "react";
import type {
    ChecklistElements,
    ChecklistObjects,
    ChecklistPartElements,
    GetChecklistPartElementsArgs,
} from "../../types";
import {Status} from "../../types";

interface ChecklistState {
    filter: {
        resetKey: number;
        statusFilterSelected: Record<string, (Status | string)[]>;
    };
    items: Record<
        string,
        {isLoading: boolean; success: boolean; error: boolean}
    >;
    elements: ChecklistElements;
    objects: ChecklistObjects;
    openItems: Record<string, boolean>;
    fetchingAll: boolean;
    fetchedAll: boolean;
    searchTerm: string;
}

export enum ChecklistEventTypes {
    CLICK_ITEM = "CLICK_ITEM",
    CLICK_TOC_ITEM = "CLICK_TOC_ITEM",
    FETCH_ALL = "FETCH_ALL",
    FETCH_ITEM = "FETCH_ITEM",
    FETCH_COMPLETE = "FETCH_COMPLETE",
    SET_SEARCH_TERM = "SET_SEARCH_TERM",
    SELECT_STATUS = "SELECT_STATUS",
    REMOVE_STATUS = "REMOVE_STATUS",
    RESET_STATUS = "RESET_STATUS",
    COLLAPSE_ALL = "COLLAPSE_ALL",
    EXPAND_VISIBLE = "EXPAND_VISIBLE",
}

export type ChecklistAction =
    | {type: ChecklistEventTypes.CLICK_ITEM; id: string; parent_id: string}
    | {type: ChecklistEventTypes.CLICK_TOC_ITEM; id: string; parent_id: string}
    | {type: ChecklistEventTypes.FETCH_ALL; id: string; tocIds: string[]}
    | {type: ChecklistEventTypes.FETCH_ITEM; id: string}
    | {
          type: ChecklistEventTypes.FETCH_COMPLETE;
          id: string;
          data?: ChecklistPartElements;
          error?: unknown;
          fetchedAll?: boolean;
          tocIds?: string[];
      }
    | {type: ChecklistEventTypes.SET_SEARCH_TERM; searchTerm: string}
    | {
          type: ChecklistEventTypes.SELECT_STATUS;
          id: string;
          status: Status | string;
      }
    | {
          type: ChecklistEventTypes.REMOVE_STATUS;
          id: string;
          status: Status | string;
      }
    | {type: ChecklistEventTypes.RESET_STATUS}
    | {type: ChecklistEventTypes.COLLAPSE_ALL}
    | {
          type: ChecklistEventTypes.EXPAND_VISIBLE;
          openItems: Record<string, boolean>;
      };

function checklistReducer(
    state: ChecklistState,
    action: ChecklistAction
): ChecklistState {
    switch (action.type) {
        case ChecklistEventTypes.CLICK_ITEM: {
            const {id} = action;
            return {
                ...state,
                openItems: {
                    ...state.openItems,
                    [id]: !state.openItems[id],
                },
            };
        }

        case ChecklistEventTypes.CLICK_TOC_ITEM: {
            const {id} = action;
            if (state.openItems[id]) return state;
            return {
                ...state,
                openItems: {
                    ...state.openItems,
                    [id]: true,
                },
            };
        }

        case ChecklistEventTypes.COLLAPSE_ALL: {
            return {
                ...state,
                openItems: {},
            };
        }

        case ChecklistEventTypes.FETCH_ALL: {
            const {id} = action;
            const idToUse = `${id}-recursive`;

            if (
                state.items[idToUse]?.isLoading ||
                (state.items[idToUse]?.success && !state.items[idToUse]?.error)
            ) {
                return state;
            }

            return {
                ...state,
                fetchingAll: true,
                items: {
                    ...state.items,
                    [idToUse]: {
                        isLoading: true,
                        success: false,
                        error: false,
                    },
                },
            };
        }

        case ChecklistEventTypes.FETCH_ITEM: {
            const {id} = action;

            if (
                state.items[id]?.isLoading ||
                (state.items[id]?.success && !state.items[id]?.error)
            ) {
                return state;
            }

            return {
                ...state,
                items: {
                    ...state.items,
                    [id]: {
                        isLoading: true,
                        success: false,
                        error: false,
                    },
                },
            };
        }

        case ChecklistEventTypes.FETCH_COMPLETE: {
            const {id, data, error, fetchedAll, tocIds} = action;

            const newItems = {...state.items};

            if (fetchedAll && tocIds) {
                const success = !!data && !error;
                tocIds.forEach((tocId) => {
                    newItems[tocId] = {
                        isLoading: false,
                        success,
                        error: !!error,
                    };
                });
            } else {
                newItems[id] = {
                    isLoading: false,
                    success: !!data && !error,
                    error: !!error,
                };
            }

            return {
                ...state,
                fetchingAll: false,
                fetchedAll: fetchedAll || state.fetchedAll,
                elements: data?.elements
                    ? {...state.elements, ...data.elements}
                    : state.elements,
                objects: data?.objects
                    ? {...state.objects, ...data.objects}
                    : state.objects,
                items: newItems,
            };
        }

        case ChecklistEventTypes.SET_SEARCH_TERM: {
            return {
                ...state,
                searchTerm: action.searchTerm,
            };
        }

        case ChecklistEventTypes.SELECT_STATUS: {
            const {id, status} = action;
            const currentFilters = state.filter.statusFilterSelected[id] || [];
            return {
                ...state,
                filter: {
                    ...state.filter,
                    statusFilterSelected: {
                        ...state.filter.statusFilterSelected,
                        [id]: [...currentFilters, status],
                    },
                },
            };
        }

        case ChecklistEventTypes.REMOVE_STATUS: {
            const {id, status} = action;
            const currentFilters = state.filter.statusFilterSelected[id] || [];
            const newFilters = currentFilters.filter((s) => s !== status);

            const newStatusFilterSelected = {
                ...state.filter.statusFilterSelected,
            };

            if (newFilters.length === 0) {
                delete newStatusFilterSelected[id];
            } else {
                newStatusFilterSelected[id] = newFilters;
            }

            return {
                ...state,
                filter: {
                    ...state.filter,
                    statusFilterSelected: newStatusFilterSelected,
                },
            };
        }

        case ChecklistEventTypes.RESET_STATUS: {
            const newResetKey = Math.floor(Date.now() / 1000);
            return {
                ...state,
                filter: {
                    ...state.filter,
                    resetKey: newResetKey,
                    statusFilterSelected: {},
                },
                openItems: {},
            };
        }

        case ChecklistEventTypes.EXPAND_VISIBLE: {
            return {
                ...state,
                openItems: action.openItems,
            };
        }

        default:
            return state;
    }
}

interface UseChecklistStateParams {
    elements?: ChecklistElements;
    objects?: ChecklistObjects;
    isPreview: boolean;
    resourceId?: string;
    getChecklistPartElements: (
        args: GetChecklistPartElementsArgs
    ) => Promise<ChecklistPartElements>;
}

export const useChecklistState = (params: UseChecklistStateParams) => {
    const {elements, objects, isPreview, resourceId, getChecklistPartElements} =
        params;

    const initialState: ChecklistState = {
        filter: {
            resetKey: new Date().getMilliseconds(),
            statusFilterSelected: {},
        },
        items: {},
        elements: elements || {},
        objects: objects || {},
        openItems: {},
        fetchingAll: false,
        fetchedAll: false,
        searchTerm: "",
    };

    const [state, dispatch] = useReducer(checklistReducer, initialState);

    const activeFetchesRef = useRef<Set<string>>(new Set());

    const fetchItem = useCallback(
        async (id: string) => {
            if (activeFetchesRef.current.has(id) || !resourceId) return;

            activeFetchesRef.current.add(id);
            dispatch({type: ChecklistEventTypes.FETCH_ITEM, id});

            try {
                const data = await getChecklistPartElements({
                    resourceId,
                    id,
                    isPreview,
                });
                dispatch({
                    type: ChecklistEventTypes.FETCH_COMPLETE,
                    id,
                    data,
                });
            } catch (error) {
                dispatch({
                    type: ChecklistEventTypes.FETCH_COMPLETE,
                    id,
                    error,
                });
            } finally {
                activeFetchesRef.current.delete(id);
            }
        },
        [resourceId, isPreview, getChecklistPartElements]
    );

    const fetchAll = useCallback(
        async (id: string, tocIds: string[]) => {
            const idToUse = `${id}-recursive`;

            if (
                activeFetchesRef.current.has(idToUse) ||
                !resourceId ||
                state.fetchedAll
            ) {
                return;
            }

            activeFetchesRef.current.add(idToUse);

            try {
                const data = await getChecklistPartElements({
                    resourceId,
                    id,
                    recursive: true,
                    isPreview,
                });
                dispatch({
                    type: ChecklistEventTypes.FETCH_COMPLETE,
                    id: idToUse,
                    data,
                    fetchedAll: true,
                    tocIds,
                });
            } catch (error) {
                dispatch({
                    type: ChecklistEventTypes.FETCH_COMPLETE,
                    id: idToUse,
                    error,
                    fetchedAll: true,
                    tocIds,
                });
            } finally {
                activeFetchesRef.current.delete(idToUse);
            }
        },
        [resourceId, isPreview, getChecklistPartElements, state.fetchedAll]
    );

    return {state, dispatch, fetchItem, fetchAll};
};
